// FILE: src/Components/AssetsData.js (FULLY UPDATED & CORRECTED)

import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from "react";
import { message, Tag, Spin, Input, Button, Popover, Select } from "antd";
import { PlusOutlined, CheckCircleFilled, CloseOutlined } from "@ant-design/icons";
import { uploadOnboardingAsset, getGoogleFonts } from "../services/assets";
import { SketchPicker } from 'react-color';

const { TextArea } = Input;
const { Option } = Select;

// --- Helper: Color Section ---
const ColorSection = ({ colors, selectedColor, onSelect, onAdd, onDelete }) => {
const [isPickerOpen, setIsPickerOpen] = useState(false);
const [newColor, setNewColor] = useState('#000000');

const handleAddColor = () => {
onAdd(newColor);
setIsPickerOpen(false);
};

return (
<div className="flex flex-wrap gap-4 items-center">
{colors.map((c, i) => (
<div key={i} className="relative group/color">
<div
className={`w-12 h-12 rounded-full cursor-pointer shadow-sm flex items-center justify-center border-2 transition-all ${selectedColor === c ? 'border-brand-primary ring-2 ring-brand-primary/20 scale-105' : 'border-gray-100 hover:border-gray-300'}`}
style={{ backgroundColor: c }}
onClick={() => onSelect(c)}
title={c}
>
{selectedColor === c && <CheckCircleFilled className="text-white drop-shadow-md text-xl" />}
</div>

<button
className="absolute top-1 right-1 bg-white text-red-500 rounded-full w-5 h-5 flex items-center justify-center shadow-md border border-gray-200 opacity-0 group-hover/color:opacity-100 transition-all duration-200 z-50 hover:bg-red-50 hover:scale-110"
onClick={(e) => { e.stopPropagation(); onDelete(c); }}
>
<CloseOutlined style={{ fontSize: '10px', fontWeight: 'bold' }} />
</button>
</div>
))}

<Popover
content={
<div className="text-center">
<SketchPicker color={newColor} onChange={(c) => setNewColor(c.hex)} disableAlpha />
<Button type="primary" size="small" className="mt-3 w-full" onClick={handleAddColor}>Add Color</Button>
</div>
}
title="Add Custom Color"
trigger="click"
open={isPickerOpen}
onOpenChange={setIsPickerOpen}
>
<div className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-brand-primary hover:text-brand-primary bg-gray-50 transition-colors text-gray-400">
<PlusOutlined className="text-lg" />
</div>
</Popover>
</div>
);
};

// --- Helper: Logo Grid ---
const LogoOption = ({ src, selected, onSelect, onDelete }) => (
<div
className={`relative group/logo w-32 h-32 border-2 rounded-xl p-3 flex items-center justify-center cursor-pointer bg-white transition-all duration-200 ${selected ? 'border-brand-primary ring-2 ring-brand-primary/10 shadow-md' : 'border-gray-100 hover:border-gray-300 hover:shadow-sm'}`}
onClick={() => onSelect(src)}
>
<img src={src} alt="Brand Logo" className="max-w-full max-h-full object-contain" />

{selected && (
<div className="absolute top-2 left-2 bg-brand-primary text-white rounded-full p-1 shadow-sm z-10 text-xs">
<CheckCircleFilled />
</div>
)}

<button
className="absolute top-2 right-2 bg-white text-red-500 rounded-full w-7 h-7 flex items-center justify-center shadow-lg border border-gray-200 opacity-0 group-hover/logo:opacity-100 transition-all duration-200 z-50 hover:bg-red-50 hover:scale-110"
onClick={(e) => { e.stopPropagation(); onDelete(src); }}
>
<CloseOutlined style={{ fontSize: '14px' }} />
</button>
</div>
);

// --- Helper: Editable Tags ---
const EditableMarketingSection = ({ title, items = [], onUpdate }) => {
const [inputValue, setInputValue] = useState('');
const [inputVisible, setInputVisible] = useState(false);
const inputRef = useRef(null);

useEffect(() => { if (inputVisible) inputRef.current?.focus(); }, [inputVisible]);

const handleClose = (removedTag) => onUpdate(items.filter((tag) => tag !== removedTag));
const showInput = () => setInputVisible(true);
const handleInputChange = (e) => setInputValue(e.target.value);
const handleInputConfirm = () => {
if (inputValue && !items.includes(inputValue)) onUpdate([...items, inputValue]);
setInputVisible(false); setInputValue('');
};

return (
<div className="mb-6">
<div className="flex justify-between items-center mb-2">
<h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide">{title}</h5>
</div>
<div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[50px]">
{items.map((item, i) => (
<Tag key={i} closable onClose={(e) => { e.preventDefault(); handleClose(item); }} className="px-3 py-1.5 text-sm rounded-md m-0 border border-gray-200 bg-white text-gray-700 flex items-center gap-1 shadow-sm">
{item}
</Tag>
))}
{inputVisible ? (
<Input ref={inputRef} type="text" size="small" style={{ width: 120, borderRadius: 6 }} value={inputValue} onChange={handleInputChange} onBlur={handleInputConfirm} onPressEnter={handleInputConfirm} />
) : (
<Tag onClick={showInput} className="cursor-pointer border-dashed border-gray-400 bg-white hover:border-brand-primary hover:text-brand-primary rounded-md px-3 py-1.5 flex items-center gap-1 transition-colors">
<PlusOutlined /> Add
</Tag>
)}
</div>
</div>
);
};

const AssetsData = forwardRef(({ dataOrganization, onDataChange, groupId }, ref) => {
const [isLoader, setIsLoader] = useState(false);
const [fonts, setFonts] = useState([]);
const logoInputRef = useRef(null);

useEffect(() => { getGoogleFonts((fontList) => setFonts(fontList)); }, []);

useImperativeHandle(ref, () => ({
getFinalAssetsData: () => {
return {
brand_logo: dataOrganization.brand_logo,
brand_color: dataOrganization.brand_color,
header_font: dataOrganization.header_font,
body_font: dataOrganization.body_font,
tone: dataOrganization.tone,
audience: dataOrganization.audience,
objectives: dataOrganization.objectives,
my_niche: dataOrganization.my_niche,
core_values: dataOrganization.core_values,
pain_points: dataOrganization.pain_points,
CTAs: dataOrganization.CTAs,
hashtags: dataOrganization.hashtags,
content: dataOrganization.content,
writing_sample: dataOrganization.writing_sample,
};
}
}));

const handleUploadLogo = async (e) => {
const file = e.target.files[0];
if (!file) return;
setIsLoader(true);
try {
const brandName = dataOrganization.Brandname || "brand";
const res = await uploadOnboardingAsset(file, brandName, groupId);
onDataChange(prev => ({
...prev,
website_image_url: [res.url, ...(prev.website_image_url || [])],
brand_logo: res.url
}));
message.success("Logo uploaded!");
} catch (err) {
message.error("Upload failed.");
} finally {
setIsLoader(false);
}
};

const handleDeleteLogo = (logoUrl) => {
onDataChange(prev => {
const newLogos = (prev.website_image_url || []).filter(l => l !== logoUrl);
return {
...prev,
website_image_url: newLogos,
brand_logo: prev.brand_logo === logoUrl ? (newLogos[0] || null) : prev.brand_logo
};
});
};

const handleAddColor = (newColor) => {
onDataChange(prev => {
const currentPalette = prev.palette ? prev.palette.split(',') : [];
const newPalette = [...currentPalette, newColor];
return {
...prev,
palette: newPalette.join(','),
brand_color: newColor,
};
});
};

const handleDeleteColor = (colorToDelete) => {
onDataChange(prev => {
const currentPalette = prev.palette ? prev.palette.split(',') : [];
const newPalette = currentPalette.filter(c => c !== colorToDelete);
return {
...prev,
palette: newPalette.join(','),
brand_color: prev.brand_color === colorToDelete ? (newPalette[0] || '#000000') : prev.brand_color
};
});
};

const logos = [...new Set([...(dataOrganization.website_image_url || []), dataOrganization.brand_logo].filter(Boolean))];
const colors = dataOrganization.palette ? dataOrganization.palette.split(',').filter(Boolean) : [];

return (
<>
{/* COLUMN 2: Visual Identity */}
<div className="h-full bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col">
<h3 className="text-lg font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100 flex items-center gap-2">
<i className="fa-solid fa-palette text-gray-400"></i> Visual Identity
</h3>
<div className="space-y-8 flex-grow">
<div>
<label className="block text-xs font-bold text-gray-500 uppercase mb-3 tracking-wide">Brand Logos</label>
<div className="flex flex-wrap gap-4">
{logos.map((src, i) => (
<LogoOption key={i} src={src} selected={dataOrganization.brand_logo === src} onSelect={(url) => onDataChange(prev => ({...prev, brand_logo: url}))} onDelete={handleDeleteLogo} />
))}
<div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-brand-primary hover:text-brand-primary bg-gray-50 transition-colors group" onClick={() => logoInputRef.current.click()}>
{isLoader ? <Spin size="small" /> : <PlusOutlined className="text-2xl mb-2 group-hover:scale-110 transition-transform" />}
<span className="text-xs font-bold uppercase tracking-wide">Upload</span>
</div>
<input type="file" ref={logoInputRef} onChange={handleUploadLogo} className="hidden" accept="image/*" />
</div>
</div>
<div>
<label className="block text-xs font-bold text-gray-500 uppercase mb-3 tracking-wide">Brand Palette</label>
<ColorSection colors={colors} selectedColor={dataOrganization.brand_color} onSelect={(color) => onDataChange(prev => ({...prev, brand_color: color}))} onAdd={handleAddColor} onDelete={handleDeleteColor} />
</div>
<div className="grid grid-cols-1 gap-4">
<div>
<label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wide">Header Font</label>
<Select showSearch style={{ width: '100%' }} size="large" placeholder="Select Font" value={dataOrganization.header_font} onChange={(font) => onDataChange(prev => ({...prev, header_font: font}))} filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}>
{fonts.map(font => <Option key={font} value={font} style={{ fontFamily: font }}>{font}</Option>)}
</Select>
</div>
<div>
<label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-wide">Body Font</label>
<Select showSearch style={{ width: '100%' }} size="large" placeholder="Select Font" value={dataOrganization.body_font} onChange={(font) => onDataChange(prev => ({...prev, body_font: font}))} filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}>
{fonts.map(font => <Option key={font} value={font} style={{ fontFamily: font }}>{font}</Option>)}
</Select>
</div>
</div>
</div>
</div>
{/* COLUMN 3: Marketing Essentials */}
<div className="h-full bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col">
<h3 className="text-lg font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100 flex items-center gap-2">
<i className="fa-solid fa-bullhorn text-gray-400"></i> Marketing Essentials
</h3>
<div className="space-y-6 flex-grow overflow-y-auto pr-2 custom-scrollbar" style={{maxHeight: 'calc(100vh - 250px)'}}>
<div>
<h5 className="text-xs font-bold text-gray-500 uppercase mb-2 tracking-wide">Tone of Voice</h5>
<TextArea rows={3} value={dataOrganization.tone || ''} onChange={(e) => onDataChange(prev => ({...prev, tone: e.target.value}))} className="bg-gray-50 border-gray-200 rounded-lg focus:bg-white transition-colors text-gray-800 text-sm" placeholder="e.g. Professional, Friendly, Witty" />
</div>
<EditableMarketingSection title="Target Audience" items={dataOrganization.audience} onUpdate={(val) => onDataChange(prev => ({...prev, audience: val}))} />
<EditableMarketingSection title="Your Niche" items={dataOrganization.my_niche} onUpdate={(val) => onDataChange(prev => ({...prev, my_niche: val}))} />
<EditableMarketingSection title="Core Values" items={dataOrganization.core_values} onUpdate={(val) => onDataChange(prev => ({...prev, core_values: val}))} />
<EditableMarketingSection title="Content Topics" items={dataOrganization.content?.map(c => c.text) || []} onUpdate={(val) => onDataChange(prev => ({...prev, content: val.map(t => ({text: t, enabled: true}))}))} />
<EditableMarketingSection title="Objectives" items={dataOrganization.objectives} onUpdate={(val) => onDataChange(prev => ({...prev, objectives: val}))} />
<EditableMarketingSection title="Pain Points" items={dataOrganization.pain_points} onUpdate={(val) => onDataChange(prev => ({...prev, pain_points: val}))} />
<EditableMarketingSection title="Hashtags" items={dataOrganization.hashtags} onUpdate={(val) => onDataChange(prev => ({...prev, hashtags: val}))} />
{/* --- START: THIS IS THE FIX --- */}
<EditableMarketingSection title="Call to Actions (CTAs)" items={dataOrganization.CTAs} onUpdate={(val) => onDataChange(prev => ({...prev, CTAs: val}))} />
<EditableMarketingSection title="Writing Samples" items={dataOrganization.writing_sample} onUpdate={(val) => onDataChange(prev => ({...prev, writing_sample: val}))} />
{/* --- END: THIS IS THE FIX --- */}
</div>
</div>
</>
);
});

export default AssetsData;